import { CloudToolsError, CloudToolsScope } from '../entities/accountEntity';
import { MutedScanError } from '../entities/accountEntity';

/**
 * True when a scan error is covered by a mute rule: the action name must
 * match, and every rule field that is set (scope, resource type, region)
 * must match too. A rule with only an action name mutes the action
 * everywhere.
 */
export function matchesMutedScanError(
  error: CloudToolsError,
  rule: MutedScanError,
): boolean {
  if (error.ActionName !== rule.actionName) {
    return false;
  }
  if (rule.scope && error.Scope !== rule.scope) {
    return false;
  }
  if (rule.resourceType && error.ResourceType !== rule.resourceType) {
    return false;
  }
  if (rule.region && !isRegionScopedError(error, rule.region)) {
    return false;
  }
  return true;
}

/**
 * Errors that survive the account's mute rules — the ones consumers should
 * count as sync problems. Muted errors stay recorded on the entity for
 * audit; they are only filtered from surfaces that report scan problems.
 */
export function filterMutedScanErrors(
  errors: CloudToolsError[] | undefined,
  mutedErrors: MutedScanError[] | undefined,
): CloudToolsError[] {
  if (!errors || errors.length === 0) {
    return [];
  }
  if (!mutedErrors || mutedErrors.length === 0) {
    return errors;
  }
  return errors.filter(
    error => !mutedErrors.some(rule => matchesMutedScanError(error, rule)),
  );
}

/**
 * Whether a region-scoped error covers the given region. Region is not a
 * field of CloudToolsError: cloud resource ids are `account_region_resource`,
 * so the region is inferred from the ids (same heuristic the backend uses
 * for the Resources page region flags).
 */
export function isRegionScopedError(
  error: CloudToolsError,
  region: string,
): boolean {
  if (error.Scope !== CloudToolsScope.REGION) {
    return false;
  }
  const normalizedRegion = region.trim().toLowerCase();
  const needle = `_${normalizedRegion}_`;
  return (error.ResourceIds ?? []).some(id => id.toLowerCase().includes(needle));
}
